import { QueryFunctionContext, useQueries, useQuery } from '@tanstack/react-query'
import { useSDK } from '@vatom/sdk/react'

import { DeviceContactsWithUserId, PublicProfileOptions, UserData } from './types'

export const publicProfileQueryKeys = {
  publicProfile: [{ scope: 'public-profile' }] as const,
  getPublicProfile: (vatomToken: string, userId?: string) =>
    [{ ...publicProfileQueryKeys.publicProfile[0], vatomToken, userId }] as const
}

export const publicProfileHost = 'https://users.vatom.com/'

const fetchUserProfile = async ({
  queryKey: [{ vatomToken, userId }]
}: QueryFunctionContext<ReturnType<typeof publicProfileQueryKeys['getPublicProfile']>>) => {
  const res = await fetch(`${publicProfileHost}${userId}`, {
    method: 'GET',
    headers: new Headers({
      Authorization: `Bearer ${vatomToken}`,
      'Content-Type': 'application/json'
    })
  })

  const data = (await res.json()) as UserData
  return data
}

export const useGetPublicProfile = <T = UserData>(
  userId?: string,
  options?: PublicProfileOptions<T>
) => {
  const sdk = useSDK()
  const getToken = () => sdk.dataPool.sessionStore.vatomIncSessionToken?.accessToken ?? ''
  const query = useQuery({
    queryKey: publicProfileQueryKeys.getPublicProfile('', userId),
    queryFn: async ctx =>
      await fetchUserProfile({
        ...ctx,
        queryKey: [
          {
            ...ctx.queryKey[0],
            vatomToken: getToken()
          }
        ]
      }),
    enabled: !!userId && options?.enabled !== false,
    ...options
  })

  return query
}

export const useGetPublicProfiles = <T = UserData[]>(
  userIds: string[],
  select?: (users: UserData) => T
) => {
  const sdk = useSDK()
  const getToken = () => sdk.dataPool.sessionStore.vatomIncSessionToken?.accessToken ?? ''
  const queries = useQueries({
    queries: userIds.map(userId => ({
      queryKey: publicProfileQueryKeys.getPublicProfile('', userId),
      queryFn: async (ctx: any) =>
        await fetchUserProfile({
          ...ctx,
          queryKey: [
            {
              ...ctx.queryKey[0],
              vatomToken: getToken()
            }
          ]
        }),
      select,
      enabled: !!userId
    }))
  })

  return queries
}

export const useGetPublicProfilesWithDevice = <T = UserData[]>(
  deviceContacts: DeviceContactsWithUserId[],
  select?: (users: UserData) => T
) => {
  const sdk = useSDK()
  const getToken = () => sdk.dataPool.sessionStore.vatomIncSessionToken?.accessToken ?? ''
  const queries = useQueries({
    queries: deviceContacts.map(contact => ({
      queryKey: publicProfileQueryKeys.getPublicProfile('', contact.userId),
      queryFn: async (ctx: any) =>
        await fetchUserProfile({
          ...ctx,
          queryKey: [
            {
              ...ctx.queryKey[0],
              vatomToken: getToken()
            }
          ]
        }),
      select: (user: UserData) => {
        const updatedUser = {
          ...user,
          name: user?.name ?? contact.deviceContact.value
        }
        return select?.(updatedUser) ?? updatedUser
      },
      enabled: !!contact.userId
    }))
  })

  return queries
}
